import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'

function trimDotSlash(value: string): string {
  return value.replace(/^[.][/\\]/, '')
}

export interface BetterstartProjectPaths {
  adminDir: string
  pagesDir: string
  schemasDir: string
  adminDbDir: string
  adminDbCoreDir: string
  adminDbCoreFile: string
  adminDbSchemaFile: string
  adminUtilsAppDir: string
  adminTypesDir: string
  adminDataFile: string
  adminDataNavigationFile: string
  adminTypesNavigationFile: string
  adminNavigationFile: string
  adminNavigationDir: string
  adminActionsDir: string
  adminEmailTemplatesDir: string
  adminHooksDir: string
}

export function resolveExtensionNamespace(schemaName: string, outputNamespace?: string): string {
  return outputNamespace ?? (schemaName === 'settings' ? 'core' : schemaName)
}

export function resolveSchemaActionDir(
  actionsDir: string,
  schemaName: string,
  outputNamespace?: string
): string {
  const namespace = resolveExtensionNamespace(schemaName, outputNamespace)
  if (namespace === 'core') {
    return path.posix.join(actionsDir, schemaName)
  }

  return namespace === schemaName
    ? path.posix.join(actionsDir, schemaName)
    : path.posix.join(actionsDir, namespace, schemaName)
}

export function resolveSchemaActionImportPath(
  schemaName: string,
  outputNamespace?: string
): string {
  const namespace = resolveExtensionNamespace(schemaName, outputNamespace)
  if (namespace === 'core') {
    return schemaName
  }

  return namespace === schemaName ? schemaName : `${namespace}/${schemaName}`
}

export function resolveProjectPaths(config: ResolvedBetterstartConfig): BetterstartProjectPaths {
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const adminDir = trimDotSlash(config.paths?.admin ?? './admin')
  const pagesDir = trimDotSlash(config.paths?.pages ?? './src/app/(admin)/admin/(authenticated)')
  const schemasDir = trimDotSlash(config.paths?.schemas ?? './admin/schemas')
  const adminDbDir = path.posix.join(adminDir, 'lib', 'db')
  const adminDbCoreDir = path.posix.join(adminDbDir, 'core')
  const adminUtilsAppDir = path.posix.join(adminDir, 'utils', 'app')
  const adminTypesDir = path.posix.join(adminDir, 'types')
  const adminNavigationFile = path.posix.join(adminDir, 'data', 'navigation.ts')
  const adminNavigationDir = path.posix.join(adminDir, 'data', 'navigation')
  const adminActionsDir = path.posix.join(adminDir, 'lib', 'actions')

  return {
    adminDir,
    pagesDir,
    schemasDir,
    adminDbDir,
    adminDbCoreDir,
    adminDbCoreFile: path.posix.join(adminDbCoreDir, 'schema.ts'),
    adminDbSchemaFile: path.posix.join(adminDbDir, 'schema.ts'),
    adminUtilsAppDir,
    adminTypesDir,
    adminDataFile: path.posix.join(adminUtilsAppDir, `${namespace.camel}.ts`),
    adminDataNavigationFile: adminNavigationFile,
    adminTypesNavigationFile: path.posix.join(adminTypesDir, 'navigation.ts'),
    adminNavigationFile,
    adminNavigationDir,
    adminActionsDir,
    adminEmailTemplatesDir: path.posix.join(adminDir, 'components', 'email-templates'),
    adminHooksDir: path.posix.join(adminDir, 'hooks')
  }
}
